/**
 * auto-image-optimizer.js
 * ------------------------------------------------------------
 * Automatically optimizes every <img> on the page:
 *   - Generates a responsive srcset/sizes for any Shopify CDN image
 *   - Adds loading="lazy" + decoding="async" (skips the first
 *     few images so your above-the-fold / LCP image isn't lazy-loaded)
 *   - Sets width/height from the image's natural size if missing,
 *     to prevent layout shift (CLS)
 *   - Works via MutationObserver too, so it also catches images
 *     added later (infinite scroll, AJAX cart, quick-view modals, etc.)
 *
 * INSTALL:
 * 1. Shopify admin -> Online Store -> Themes -> Edit code
 * 2. Under "Assets", click "Add a new asset" -> Create a blank file
 *    -> name it "auto-image-optimizer.js" -> paste this code in
 * 3. Open layout/theme.liquid, find the closing </body> tag, and add
 *    just above it:
 *      <script src="{{ 'auto-image-optimizer.js' | asset_url }}" defer></script>
 * 4. Save. That's it — no need to touch individual image tags.
 *
 * CONFIG: adjust the constants below if needed.
 */
(function () {
  'use strict';

  // Number of images (in DOM order) to treat as "above the fold" —
  // these are NOT lazy-loaded, so your hero/LCP image loads immediately.
  var EAGER_IMAGE_COUNT = 2;

  // Widths to generate in the srcset for Shopify CDN images.
  var SRCSET_WIDTHS = [200, 400, 600, 800, 1000, 1200, 1600, 2000];

  // Default sizes attribute if none is set on the element.
  var DEFAULT_SIZES = '100vw';

  var processedImages = new WeakSet();
  var eagerCount = 0;

  function isShopifyCdnUrl(url) {
    return /\/\/cdn\.shopify\.com\/.+\/(files|products|collections)\//.test(url) ||
           /shopifycdn|cdn\.shopify\.com/.test(url);
  }

  // Strip any existing width/height/format query params so we can rebuild them
  function baseUrl(url) {
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      return u.toString();
    } catch (e) {
      return url.split('?')[0];
    }
  }

  function buildSrcset(url) {
    var base = baseUrl(url);
    var separator = base.indexOf('?') > -1 ? '&' : '?';
    return SRCSET_WIDTHS.map(function (w) {
      return base + separator + 'width=' + w + ' ' + w + 'w';
    }).join(', ');
  }

  function optimizeImage(img) {
    if (processedImages.has(img)) return;
    if (!img.src && !img.getAttribute('data-src')) return;

    processedImages.add(img);

    var currentSrc = img.currentSrc || img.src || img.getAttribute('data-src');
    if (!currentSrc) return;

    // Only auto-generate srcset for Shopify-hosted images we can safely resize
    if (isShopifyCdnUrl(currentSrc) && !img.srcset) {
      img.srcset = buildSrcset(currentSrc);
      if (!img.sizes) {
        img.sizes = img.getAttribute('data-sizes') || DEFAULT_SIZES;
      }
    }

    // Lazy-load everything except the first few (LCP-safe)
    if (!img.hasAttribute('loading')) {
      if (eagerCount < EAGER_IMAGE_COUNT) {
        img.setAttribute('loading', 'eager');
        img.setAttribute('fetchpriority', 'high');
        eagerCount++;
      } else {
        img.setAttribute('loading', 'lazy');
      }
    }

    if (!img.hasAttribute('decoding')) {
      img.setAttribute('decoding', 'async');
    }

    // Prevent layout shift: set width/height if missing, once the image loads
    if (!img.getAttribute('width') || !img.getAttribute('height')) {
      if (img.complete && img.naturalWidth) {
        img.setAttribute('width', img.naturalWidth);
        img.setAttribute('height', img.naturalHeight);
      } else {
        img.addEventListener('load', function onLoad() {
          if (img.naturalWidth) {
            img.setAttribute('width', img.naturalWidth);
            img.setAttribute('height', img.naturalHeight);
          }
          img.removeEventListener('load', onLoad);
        });
      }
    }
  }

  function optimizeAllImages(root) {
    var imgs = (root || document).querySelectorAll('img');
    imgs.forEach(optimizeImage);
  }

  // Run on initial load
  document.addEventListener('DOMContentLoaded', function () {
    optimizeAllImages(document);
  });

  // Catch images injected later (cart drawer, quick view, infinite scroll, etc.)
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'IMG') {
          optimizeImage(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('img').forEach(optimizeImage);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
